import clsx from 'clsx';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTIVE_TAB_PARAM, AppRoutes, INTEGRATION_SERVICE_TO_AUTH_KEY } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { IntegrationService, Permissions, SettingsTab, UploadMediaType } from '../../../types';
import { capitalizeFirstLetter, labelToKebabCase } from '../../../utils';
import { ButtonColor, Icon, Icons, TextButton, Typography, TypographyType } from '../../shared';

const INTEGRATION_CONFIGS = {
  [IntegrationService.GONG]: {
    icon: Icon.GONG,
    buttonColor: ButtonColor.GONG,
    uploadMediaType: UploadMediaType.GONG_CALL,
  },
  [IntegrationService.SALESLOFT]: {
    icon: Icon.SALESLOFT,
    buttonColor: ButtonColor.SALESLOFT,
    uploadMediaType: UploadMediaType.SALESLOFT_CALL,
  },
};

interface ImportFromIntegrationCardProps {
  service: IntegrationService;
  setMediaType: Dispatch<SetStateAction<UploadMediaType | undefined>>;
  className?: string;
  selectCallButtonDisabled?: boolean;
  selectCallButtonDisabledTooltip?: string;
}

const ImportFromIntegrationCard = ({
  service,
  setMediaType,
  selectCallButtonDisabled,
  selectCallButtonDisabledTooltip,
  className,
}: ImportFromIntegrationCardProps) => {
  const navigate = useNavigate();
  const { organization, user } = useAppSelector((state) => state.auth);

  const integrationConfig = INTEGRATION_CONFIGS[service];
  const { icon, buttonColor, uploadMediaType } = integrationConfig;
  const integrationAuthKey = INTEGRATION_SERVICE_TO_AUTH_KEY[service];
  const integrationName = capitalizeFirstLetter(service);

  const isIntegrationConnected = !!organization?.[integrationAuthKey];
  const permissions = user?.permissions || [];
  const canConnectExternalIntegrations = permissions.includes(Permissions.CONNECT_EXTERNAL_INTEGRATION);

  const sectionTitle = isIntegrationConnected ? `Import from ${integrationName}` : `Connect ${integrationName}`;

  const handleConnectIntegration = useCallback(() => {
    const searchParams = new URLSearchParams();
    searchParams.set(ACTIVE_TAB_PARAM, labelToKebabCase(service));

    navigate({
      pathname: AppRoutes.SETTINGS + '/' + SettingsTab.INTEGRATIONS,
      search: searchParams.toString(),
    });
  }, [service, navigate]);

  return (
    <div className={clsx('flex justify-between gap-2 p-4', className)}>
      <div className="flex items-center gap-2">
        <Icons icon={icon} color="text-transparent" preserveSize />
        <Typography type={TypographyType.H3}>{sectionTitle}</Typography>
      </div>
      {isIntegrationConnected && (
        <TextButton
          text="Select call"
          color={buttonColor}
          disabled={selectCallButtonDisabled}
          tooltip={selectCallButtonDisabledTooltip}
          onClick={() => setMediaType(uploadMediaType)}
        />
      )}
      {!isIntegrationConnected && (
        <TextButton
          text="Connect"
          color={buttonColor}
          disabled={!canConnectExternalIntegrations}
          onClick={handleConnectIntegration}
        />
      )}
    </div>
  );
};

export default ImportFromIntegrationCard;
